import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    IAccount,
    IAccountMappingTemplate,
    IAopCcr,
    IAopProfitCalculation,
    IAopProposal,
    IAppropriationOfProfit,
    IStyleOptions,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { IAopSelectedAccountPrintPreviewMappingValue } from '../../interfaces/IAopSelectedAccountPrintPreviewMappingValue.interface';

const debug = Debug('aumico:aumico-lib-annualreport-fe:appropriationOfProfitPage');
@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'aumico-annualReport-appropriation-of-profit-page',
    templateUrl: 'appropriation-of-profit.page.html',
    styleUrls: ['appropriation-of-profit.page.scss'],
})
export class AppropriationOfProfitPage implements OnChanges {
    private aopDescriptionValue = '';
    savedAopDescription: string | undefined;

    @Input() balanceAccounts: IAccount[] = [];
    @Input() equityAccounts: IAccount[] = [];
    @Input() previousCcr: IAopCcr | undefined;
    @Input() previousShowCcrTable: boolean | undefined;
    @Input() balanceTemplate: IAccountMappingTemplate | undefined;

    @Input() currentAnnualProfit: number | null = null;
    @Input() previousAnnualProfit: number | null = null;
    @Input() currentYearDate: string | undefined;
    @Input() previousYearDate: string | undefined;
    @Input() currency: string;
    @Input() isSavedBefore = false;
    currentAvailableRetainedEarnings: number | undefined;
    previousAvailableRetainedEarnings: number | undefined;

    @Input() removeEmptyLines: boolean;

    @Input()
    stylePreferenceOptions: IStyleOptions;

    // we set appropriationData as input incase of edit mode
    @Input() appropriationData: IAppropriationOfProfit | undefined;
    // refer to finNumber pipe for reference on values
    @Input() numberFormat = '1.2-2';

    @Input() selectedAccount: IAopSelectedAccountPrintPreviewMappingValue | undefined;

    @Input() previousProfitCalculation: IAopProfitCalculation | undefined;

    @Input() previousProposal: IAopProposal | undefined;

    @Input() hasNoPreviousData = false;

    @Input() reportLocale: string;

    @Input() previousAopDescription: string;

    // for saved proposal that comes from appropriationData input
    savedProposal: IAopProposal | undefined;
    // for saved profit calculation that comes from appropriationData input
    savedProfitCalculation: IAopProfitCalculation | undefined;
    // for saved CCR that comes from appropriationData input
    savedCcr: IAopCcr | undefined;

    showCcrTable = false;

    private ccrOutputData: IAopCcr | undefined;
    private ccrPrefilledValueData: IAopCcr | undefined;

    private profitCalculationOutput: IAopProfitCalculation | undefined;

    private profitCalculationPrefilledValue: IAopProfitCalculation | undefined;

    private proposalOutput: IAopProposal | undefined;

    private proposalPrefilledValue: IAopProposal | undefined;

    private appropriationOfProfit: IAppropriationOfProfit | null = null;

    private appropriationOfProfitPrefilledValue: IAppropriationOfProfit | null = null;

    private isDirty = false;

    @Output() appropriationOfProfitOutput = new EventEmitter<IAppropriationOfProfit>();
    @Output() appropriationOfProfitPrefilledValueOutput = new EventEmitter<IAppropriationOfProfit>();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['appropriationData']) {
            if (
                changes['appropriationData'].currentValue != null &&
                changes['appropriationData'].currentValue.profitCalculationOutput != null
            ) {
                this.savedProfitCalculation = changes['appropriationData'].currentValue.profitCalculationOutput;

                const { aopDescription } = changes['appropriationData'].currentValue;
                if (aopDescription) {
                    this.savedAopDescription = aopDescription;
                    this.aopDescriptionValue = aopDescription;
                }
            }
            if (
                changes['appropriationData'].currentValue != null &&
                changes['appropriationData'].currentValue.proposalOutput != null
            ) {
                this.savedProposal = changes['appropriationData'].currentValue.proposalOutput;
            }
            if (changes['appropriationData'].currentValue != null) {
                const appData = changes['appropriationData'].currentValue;
                if (appData.ccrOutput != null) {
                    this.savedCcr = appData.ccrOutput;
                    this.showCcrTable = appData.showCcrTable;
                }
            }
        }

        if (this.previousCcr != null && changes['previousShowCcrTable']) {
            this.showCcrTable = changes['previousShowCcrTable'].currentValue === true;
        }

        // previousAopDescription is forwarded down to ProposalComponent via @Input binding
    }

    onProfitCalculationChange(profitCalculation: IAopProfitCalculation): void {
        this.isDirty = true;
        this.profitCalculationOutput = cloneDeep<IAopProfitCalculation>(profitCalculation);
        this.emitAopOutput();
    }

    onProfitCalculationPrefilledValuePrepared(profitCalculation: IAopProfitCalculation): void {
        this.profitCalculationPrefilledValue = profitCalculation;

        if (this.proposalPrefilledValue == null) {
            return;
        }

        this.appropriationOfProfitPrefilledValue = {
            annualProfit: this.currentAnnualProfit,
            profitCalculationOutput: this.profitCalculationPrefilledValue,
            proposalOutput: this.proposalPrefilledValue,
            aopDescription: this.aopDescriptionValue,
            ccrOutput: this.ccrPrefilledValueData,
        };

        if (!this.isDirty) {
            this.appropriationOfProfitPrefilledValueOutput.emit(this.appropriationOfProfitPrefilledValue);
        }
    }

    onProfitCalculationCurrentRetainedEarningsChange(currentAvailableRetainedEarnings: number): void {
        this.currentAvailableRetainedEarnings = currentAvailableRetainedEarnings;
    }

    onProfitCalculationPreviousRetainedEarningsChange(previousAvailableRetainedEarnings: number): void {
        this.previousAvailableRetainedEarnings = previousAvailableRetainedEarnings;
    }

    onProposalOutputChange(proposalOutput: IAopProposal): void {
        this.isDirty = true;
        this.proposalOutput = cloneDeep<IAopProposal>(proposalOutput);
        this.emitAopOutput();
    }

    onProposalPrefilledValuePrepared(proposalOutput: IAopProposal): void {
        this.proposalPrefilledValue = proposalOutput;
        if (this.profitCalculationPrefilledValue == null) {
            return;
        }
        this.appropriationOfProfitPrefilledValue = {
            annualProfit: this.currentAnnualProfit,
            profitCalculationOutput: this.profitCalculationPrefilledValue,
            proposalOutput: this.proposalPrefilledValue,
            aopDescription: this.aopDescriptionValue,
            ccrOutput: this.ccrPrefilledValueData,
        };
        if (!this.isDirty) {
            this.appropriationOfProfitPrefilledValueOutput.emit(this.appropriationOfProfitPrefilledValue);
        }
    }

    toggleCcrTable(): void {
        this.showCcrTable = !this.showCcrTable;
        this.emitAopOutput();
    }

    onCcrOutputChange(ccr: IAopCcr): void {
        this.isDirty = true;
        this.ccrOutputData = cloneDeep(ccr);
        this.emitAopOutput();
    }

    onCcrPrefilledValuePrepared(ccr: IAopCcr): void {
        this.ccrPrefilledValueData = ccr;

        // Seed ccrOutputData from the prefill so that emitAopOutput() (which uses
        // ccrOutputData, not ccrPrefilledValueData) includes the CCR even when the
        // user never edits the CCR table.  This is the only place where CCR data
        // arrives during initialisation — onCcrOutputChange only fires on user edits.
        // NOTE: do NOT guard with isDirty here — onProfitCalculationChange and
        // onProposalOutputChange both set isDirty=true before CCR (behind *ngIf)
        // gets a chance to render, so isDirty is already true by the time this fires.
        this.ccrOutputData = ccr;

        // The CCR component is rendered behind *ngIf="showCcrTable", so it initialises
        // after the proposal/profitCalculation outputs have already been emitted — without
        // ccrOutput.  Re-emit both the regular output and the prefilled output now that
        // we have all three pieces.
        this.emitAopOutput();

        if (
            !this.isDirty &&
            this.profitCalculationPrefilledValue != null &&
            this.proposalPrefilledValue != null
        ) {
            this.appropriationOfProfitPrefilledValue = {
                annualProfit: this.currentAnnualProfit,
                profitCalculationOutput: this.profitCalculationPrefilledValue,
                proposalOutput: this.proposalPrefilledValue,
                aopDescription: this.aopDescriptionValue,
                ccrOutput: this.ccrPrefilledValueData,
            };
            this.appropriationOfProfitPrefilledValueOutput.emit(this.appropriationOfProfitPrefilledValue);
        }
    }

    private emitAopOutput(): void {
        if (this.profitCalculationOutput == null || this.proposalOutput == null) {
            return;
        }
        this.appropriationOfProfit = {
            annualProfit: this.currentAnnualProfit,
            profitCalculationOutput: this.profitCalculationOutput,
            proposalOutput: this.proposalOutput,
            aopDescription: this.aopDescriptionValue,
            showCcrTable: this.showCcrTable,
            ccrOutput: this.ccrOutputData,
        };
        debug('aop output', this.appropriationOfProfit);
        this.appropriationOfProfitOutput.emit(this.appropriationOfProfit);
    }

    onAopDescriptionChange(value: string): void {
        this.isDirty = true;
        this.aopDescriptionValue = value;
        this.emitAopOutput();
    }

}
